import { ProcedureInstanceBase, PROCEDURETYPE } from "@/backend/models/procedureInstances/procedureInstanceBase";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { resize } from "@/frontend/utils/parseReferences";
import { I18n } from "@/i18n/I18n";
import { copyPasteTypes } from "@/types/copyPasteTypes";
import { ParameterType, ParameterSubjectType } from "@/types/parameterTypes";
import { useState, useEffect } from "react";
import { Dropdown, Form } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { BasicButton } from "../buttons/BasicButton";
import { CollapseButton } from "../buttons/CollapseButton";
import { ContainerButtonRow } from "../containers/ContainerButtonRow";
import { SectionItem } from "../containers/Section";
import { CustomToggle } from "../dropdown/CustomToggle";
import { DeleteItem } from "../dropdown/DeleteItem";
import { OrderItem } from "../dropdown/OrderItem";
import { PasteItem } from "../dropdown/PasteItem";
import { ParameterTable } from "../parameters/ParameterTable";
import { SideNav } from "../sideNav/SideNav";
import { useProcedureListStore } from "@/frontend/store/ProcedureListStore";
import { useProcedureInstanceStore } from "@/frontend/store/ProcedureInstanceStore";
import { scrollToTop } from "@/frontend/utils/scrollTo";
import { ProcedureTabsNavigation } from "./ProcedureTabsNavigation";
import { IntroductionAsset } from "../parameters/IntroductionAsset";
import { AddIntroductionAssetButton } from "../parameters/AddIntroductionAsset";
import { FunctionalInterfacesTable } from "./FunctionalInterfacesTable";
import { useProcedureStore } from "@/frontend/store/ProcedureStore";
import { VersionStatus } from "@/types/genericVersionTypes";
import { EquipmentTable } from "../equipment/EquipmentTable";
import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { FusawarePermission } from "@/frontend/fusawareUser";
import { useProjectStore } from "@/frontend/store/ProjectStore";
import { ProcedurePlaceholder } from "../containers/ContainersPlaceholder";
import { CollapsibleAddRow } from "../buttons/CollapsibleAddRow";

export function ProcedureItem() {
  // const exportProcedure = useProcedureStore((state) => state.exportProcedure);
  const deleteProcedureItem = useProcedureListStore((state) => state.deleteProcedureItem);
  const orderContainers = useTreeStore((state) => state.orderContainers);
  const updateProcedure = useProcedureInstanceStore((state) => state.updateProcedureInstance);
  const exportJson = useProcedureInstanceStore((state) => state.exportJson);
  const procedure = useProcedureInstanceStore((state) => state.procedureInstance) as ProcedureInstanceBase;
  const rootContainerNodes = useTreeStore((state) => state.templateRootContainerNodes);
  const sections = rootContainerNodes.map(node => node.container);
  const [showProcedure, setShowProcedure] = useState(true);
  const navigator = useNavigate();
  // const ollamaAvailable = useGlobalStore((state) => state.isOllamaAvailable);
  const [description, setDescription] = useState(procedure.description);
  const [error, setError] = useState<string | null>(null);
  const [isEditable, setIsEditable] = useState(true);
  const fetchVersion = useProcedureStore((state) => state.fetchVersion);
  const [estimatedRunTime, setEstimatedRunTime] = useState(procedure.estimatedRunTime);
  const [title, setTitle] = useState(procedure.title);
  const user = useGlobalStore((state) => state.userClass);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const fetchProcedureVersion = async () => {
      if(!procedure.versionId) return;
      const version = await fetchVersion(procedure.versionId);
      if(version?.status === VersionStatus.PUBLISHED) {
        setIsEditable(false);
      }
    };
    void fetchProcedureVersion().finally(() => {
      setLoading(false);
    });
  }, [procedure.versionId, fetchVersion]);
  
  useEffect(() => {
    resize(`description-${procedure.id}`);
  }, [procedure.id, showProcedure]);

  const parts = useProjectStore(state => state.projectParts);
  const fetchPartsForProcedureInstance = useProjectStore(state => state.fetchPartsForProcedureInstance);
  useEffect(() => {
    void fetchPartsForProcedureInstance(procedure.id);
  }, [procedure.id, fetchPartsForProcedureInstance]);

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    resize(`description-${procedure.id}`);
  };

  const deleteAction = async () => {
    await deleteProcedureItem(procedure);
    if (procedure.procedureType === PROCEDURETYPE.PROCEDURE) {
      void navigator('/procedures');
    } else {
      void navigator('/procedure-templates');
    }
  };

  const updateTitle = (title: string) => {
    if(!title) {
      setError(I18n.get('requiredFieldError', { field: I18n.get('title') }));
      return;
    }
    setError(null);
    void updateProcedure({ procedureInstance: { ...procedure, title } });
  };

  // const handleOllamaComplete = (response: string) => {
  //   setDescription(response);
  //   onChangeText('description', response);
  //   resize(`description-${procedure.id}`);
  // };

  if(!isEditable) return (
    <div className="d-flex flex-column align-items-center justify-content-center w-100 h-100">
      <h1>{I18n.get('procedureNotEditable', { title: procedure.title })}</h1>
      <p>{I18n.get('procedureNotEditableDescription')}</p>
      <BasicButton onClick={() => void navigator('/procedure-templates')} label={I18n.get('backToTemplates')} className='btn btn-primary' />
    </div>
  );

  if (loading) {
    return <ProcedurePlaceholder />;
  }

  const link = procedure.isTemplate() ? '/procedure-templates' : '/procedures';
  const linkText = procedure.isTemplate() ? I18n.get('templates') : I18n.get('procedures');

  return (
    <div className="w-100 d-flex h-100" style={{position: 'relative', maxHeight: '100vh', height: '100vh'}} data-bs-spy="scroll" data-bs-target="#navbarScroller" data-bs-offset="0">
        <div className='mh-100'>
          <SideNav rootName={procedure.title} isScaffold link={<Link to={link}>{linkText}</Link>} hideProgress />
        </div>
        <div className='flex-fill mh-100 d-flex flex-column'>
          <ProcedureTabsNavigation />
          <div id='disable-container' className='overflow-scroll mh-100 flex-fill px-2'>
            <div className="d-flex justify-content-between w-100 mh-100">
            <CollapseButton
              showElement={showProcedure}
              setShowElement={setShowProcedure}
              title={I18n.get('template')}
            />
            <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
              <Dropdown drop='down'>
                <Dropdown.Toggle size='sm' as={CustomToggle(copyPasteTypes.Procedure)} variant='dark' id="dropdown-basic" />
                <Dropdown.Menu>
                  <OrderItem listOfItems={sections} orderItems={orderContainers} instance={copyPasteTypes.Section} />
                  <PasteItem item={procedure} instance={copyPasteTypes.Procedure} isScaffold />
                  {user?.fusaHasPermission(FusawarePermission.EXPORT_PROCEDURE_JSON) && <Dropdown.Item key={`${procedure.id}export-json`} onClick={() => void exportJson(procedure.id)}>
                    {I18n.get('exportJson')}
                  </Dropdown.Item>}
                  {procedure.isProcedure() && <DeleteItem deleteAction={deleteAction} itemType={copyPasteTypes.Procedure} title={procedure.title} /> }
                  
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className='w-100'>
              <Form.Label className="col-form-label col-form-label-sm">{I18n.get('Title')}</Form.Label>
              <Form.Control 
                type="text"
                size='sm' 
                value={title} 
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => void updateTitle(title)}
                isInvalid={!!error}
              />
            </div>
            <div className="pt-2">
              <Form.Label className="col-form-label col-form-label-sm">{I18n.get('estimatedRunTime')}</Form.Label>
              <Form.Control 
                type="text"
                size='sm' 
                value={estimatedRunTime}
                onChange={(e) => setEstimatedRunTime(e.target.value)}
                onBlur={(e) => void updateProcedure({ procedureInstance: { ...procedure, estimatedRunTime: e.target.value } })}
              />
            </div>
            <div className="card-body">
              <Form.Label className="col-form-label col-form-label-sm">{I18n.get('procedureDescription')}</Form.Label>
              <Form.Control
                as="textarea"
                id={`description-${procedure.id}`}
                value={description}
                onChange={(e) => void handleDescriptionChange(e.target.value)}
                onBlur={() => void updateProcedure({ procedureInstance: { ...procedure, description } })}
                className="overflow-scroll "
                style={{
                  minHeight: '6em',
                  maxHeight: '18em',
                  resize: 'vertical'
                }}
              />
            </div>
            <div className="d-flex flex-column gap-2">
              <AddIntroductionAssetButton />
              <IntroductionAsset editView />
            </div>
            <div className="mt-4 d-flex flex-column gap-4">
              { parts.length > 0 && (
                <div className='card'>
                  <div className='card-header'>{I18n.get('deviceUnderTest')}</div>
                  <div className='card-body'>
                    { parts.map(part => {
                      return (
                        <div key={part.id}>
                          <div>{part.title}</div>
                          { part.description && <div className='text-muted'>{part.description}</div> }
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <EquipmentTable editable parentShowCascade={showProcedure} />
              {/* <ProcedureLabelling parentShowCascade={showProcedure} /> */}
            </div>
            {user?.fusaHasPermission(FusawarePermission.ADD_INTERFACES_TO_TEMPLATES) && <div className="mt-4"><FunctionalInterfacesTable /></div>}
            <div className='mt-4'><ParameterTable subjectId={procedure.id} parameterType={ParameterType.CONFIG} subjectType={ParameterSubjectType.PROCEDURE} /></div>
            <div className='mt-4'><ParameterTable subjectId={procedure.id} parameterType={ParameterType.INPUT} subjectType={ParameterSubjectType.PROCEDURE} hideAddButton /></div>
            <div className="mt-4">
              <CollapsibleAddRow label={I18n.get('addContainerHere')} defaultOpen={rootContainerNodes.length === 0}>
                <ContainerButtonRow parent={procedure} position={0} hideStep isScaffold />
              </CollapsibleAddRow>
            </div>
            <div className="mb-4">
              <div className="">
                {rootContainerNodes.map(rootContainerNode => {
                  return (
                    <div key={rootContainerNode.id}>
                      <SectionItem
                        key={rootContainerNode.id}
                        initItem={rootContainerNode}
                        parentShowCascade={showProcedure}
                      />
                      <CollapsibleAddRow label={I18n.get('addContainerHere')}>
                        <ContainerButtonRow parent={procedure} position={rootContainerNode.container.position + 1} isScaffold hideStep/>
                      </CollapsibleAddRow>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="">
              <div className="text-center">
                <BasicButton onClick={scrollToTop} label={I18n.get('backToTop')} className='btn d-print-none' />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
